<template>
    <v-dialog
        :value="isOpen"
        max-width="1400px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <StatusHistoryDialog
            :accident-status-history="accidentStatusHistory"
            :is-open="statusHistoryDialog"
            @close-status-history-dialog="closeStatusHistoryDialog"
        />
        <QrCodeDialog
            :is-open="qrCodeDialog"
            :text="qrCodeText"
            @close-qr-code-dialog="closeQrCodeDialog"
        >
            <template #title>
                {{ qrCodeTitle }}
            </template>
        </QrCodeDialog>
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Bieżące awarie na stanowisku
                </h1>
            </v-card-title>
            <v-data-table
                :headers="headers"
                :items="workspaceAccidentsListData"
                :mobile-breakpoint="0"
                name="workspaceAccidentsListData"
            >
                <template #item.machine="{item}">
                    {{ item.machine.name }}
                </template>
                <template #item.createdAt="{item}">
                    {{ formatDate(item.createdAt) }}
                </template>
                <template #item.status="{item}">
                    {{ item.accidentStatus.name }}
                </template>
                <template #item.type="{item}">
                    {{ item.accidentType.name }}
                </template>
                <template #item.department="{item}">
                    {{ item.accidentDepartment.name }}
                </template>
                <template #item.creator="{item}">
                    <template v-if="item.creator.firstName && item.creator.lastName">
                        {{ item.creator.firstName }} {{ item.creator.lastName }}
                    </template>
                    <template v-else>
                        {{ item.creator.username }}
                    </template>
                </template>
                <template #item.lastAssignee="{item}">
                    <template v-if="item.lastAssignee">
                        <template v-if="item.lastAssignee.firstName && item.lastAssignee.lastName">
                            {{ item.lastAssignee.firstName }} {{ item.lastAssignee.lastName }}
                        </template>
                        <template v-else>
                            {{ item.lastAssignee.username }}
                        </template>
                    </template>
                    <template v-else>
                        -
                    </template>
                </template>
                <template #item.actions="{item}">
                    <td>
                        <div class="d-inline-flex">
                            <v-tooltip :open-delay="tooltipDelay" max-width="500px" left>
                                <template #activator="{on}">
                                    <v-icon
                                        color="blue"
                                        class="mx-1"
                                        v-on="on"
                                    >
                                        mdi-note-text-outline
                                    </v-icon>
                                </template>
                                <span>{{ item.description }}</span>
                            </v-tooltip>
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Historia"
                                        v-on="on"
                                        @click="getAccidentStatusHistory(item.id)"
                                    >
                                        <v-icon color="blue-grey">
                                            mdi-history
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Historia</span>
                            </v-tooltip>
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Generuj kod QR"
                                        v-on="on"
                                        @click="generateQrCode(item)"
                                    >
                                        <v-icon color="black">
                                            mdi-qrcode
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Generuj kod QR</span>
                            </v-tooltip>
                        </div>
                    </td>
                </template>
            </v-data-table>
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import QrCodeDialog from '../../../components/common/QrCodeDialog'
import parse from '@/mixins/parse.js'
import StatusHistoryDialog from '../../Maintenance/Accidents/StatusHistoryDialog'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'WorkspaceAccidentsListDialog',
    components: {
        StatusHistoryDialog,
        QrCodeDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        workspaceAccidentsListData: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            accidentStatusHistory: [],
            currentAccident: null,
            statusHistoryDialog: false,
            qrCodeDialog: false,
            headers: [
                {
                    text: 'Maszyna',
                    value: 'machine',
                    sortable: false,
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Status',
                    value: 'status',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Typ',
                    value: 'type',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Dział',
                    value: 'department',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Zgłaszający',
                    value: 'creator',
                    align: 'center',
                    width: 180,
                    sortable: false,
                },
                {
                    text: 'Prowadzący',
                    value: 'lastAssignee',
                    align: 'center',
                    width: 180,
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 50
                }
            ]
        }
    },
    computed: {
        qrCodeText() {
            return `{ "type": "ZA", "uniqueToken": "${this.currentAccident?.uniqueToken}"}`
        },
        qrCodeTitle() {
            return `Awaria ${this.currentAccident?.id}`
        }
    },
    methods: {
        getAditionalData(accident) {
            return {'number': accident.number}
        },
        onClose() {
            this.$emit('on-close')
        },
        getAccidentStatusHistory(id) {
            this.$store.dispatch('maintenance/getAccidentStatusHistory', id)
                .then(response => {
                    this.accidentStatusHistory = response.data.results
                    this.statusHistoryDialog = true
                }).catch(error => {
                    errorHandler(error)
                })
        },
        closeStatusHistoryDialog() {
            this.statusHistoryDialog = false
        },
        generateQrCode(accident) {
            this.currentAccident = accident,
            this.qrCodeDialog = true
        },
        closeQrCodeDialog() {
            this.qrCodeDialog = false
        },
    }
}
</script>